import { LngLatBounds, LngLat, ImageSource } from 'maplibre-gl'
import { MapRef } from 'react-map-gl/maplibre';
import { MutableRefObject} from 'react'
import { Board, ExpandedSlap, Coordinates, Place, PlaceGroup, SlapId } from '../utils/Primitives';
import { lookupSlap } from '../slaps/Slaps';
import { getStickerDesign, getStickerUrl } from '../stickers/StickerDesigns';
import { getPlaceFrameCoordinates } from '../places/Places'
import { BlockPlaces } from '@ivyroot/block-places';
import { coordinatesForPendingButton, coordinatesForInProgressLabelInPlace } from '../utils/LatLongTools';

export const sourceIdForSlap = (slapId: number) => {
    return `slap-src-${slapId}`
}

export const layerIdForSlap = (slapId: number) => {
    return `slap-lyr-${slapId}`
}

export const extractSlapIdFromLayerId = (layerId: string) : Number | null => {
    if (!layerId.includes('slap-lyr-')) {
        return null
    }
    const parts = layerId.split('-')
    return Number(parts[2])
}

export const execButtonSourceIdForSlap = (slapId: number) => {
    return `exslap-btn-src-${slapId}`
}

export const execButtonLayerIdForSlap = (slapId: number) => {
    return `exslap-btn-lyr-${slapId}`
}

export const inProgressLabelSourceIdForSlap = (slapId: number) => {
    return `inprog-src-${slapId}`
}

export const inProgressLabelLayerIdForSlap = (slapId: number) => {
    return `inprog-lyr-${slapId}`
}

export const pendingOverlaySourceIdForSlap = (slapId: number) => {
    return `pending-overlay-src-${slapId}`
}

export const pendingOverlayLayerIdForSlap = (slapId: number) => {
    return `pending-overlay-lyr-${slapId}`
}

export const deleteLayerForSlap = (props:{mapRef?: MutableRefObject<MapRef | null>, slapId: SlapId}) => {
    const { mapRef, slapId } = props
    if (!mapRef) {
        return;
    }
    if (!mapRef.current) {
        return;
    }
    const map = mapRef.current.getMap();
    if (!map) {
        return;
    }
    const slapSourceIdString = sourceIdForSlap(slapId)
    const slapLayerId = layerIdForSlap(slapId)
    const placeExecuteButtonSourceId = execButtonSourceIdForSlap(slapId)
    const placeExecuteButtonLayerId = execButtonLayerIdForSlap(slapId)
    const inProgressLabelSourceId = inProgressLabelSourceIdForSlap(slapId)
    const inProgressLabelLayerId = inProgressLabelLayerIdForSlap(slapId)
    const pendingOverlaySourceId = pendingOverlaySourceIdForSlap(slapId)
    const pendingOverlayLayerId = pendingOverlayLayerIdForSlap(slapId)

    // NB: not actually deleting layers
    // because async write to board recreates them in race condition
    if (map.getLayer(placeExecuteButtonLayerId)) {
        map.setLayoutProperty(placeExecuteButtonLayerId, 'visibility', 'none')
    }
    if (map.getLayer(slapLayerId)) {
        map.setLayoutProperty(slapLayerId, 'visibility', 'none')
    }
    if (map.getLayer(inProgressLabelLayerId)) {
        map.setLayoutProperty(inProgressLabelLayerId, 'visibility', 'none')
    }
    if (map.getLayer(pendingOverlayLayerId)) {
        map.setLayoutProperty(pendingOverlayLayerId, 'visibility', 'none')
    }
    // map.removeSource(slapSourceIdString)
    // map.removeSource(placeExecuteButtonSourceId)
    // map.removeSource(inProgressLabelSourceId)
    // map.removeSource(pendingOverlaySourceId)
}


export const renderLayerForSlap = (props: {
    mapRef?: MutableRefObject<MapRef | null>
    boardRef: MutableRefObject<Board>,
    slapId: SlapId,
}) => {
    const { mapRef, boardRef, slapId } = props
    
    // Guard against undefined/null slapId
    if (slapId === undefined || slapId === null) {
        console.log(`SLAP LOOKUP ERROR: slapId is ${slapId}, skipping render`)
        return;
    }
    
    if (!mapRef) {
        return;
    }
    if (!mapRef.current) {
        return;
    }
    const map = mapRef.current.getMap();
    if (!map) {
        return;
    }
    const style = map.getStyle();
    if (!style) {
        return;
    }
    const foundSlap = lookupSlap({boardRef, slapId: slapId})
    if (!foundSlap) {
        console.log(`SLAP LOOKUP ERROR FOR ${slapId} RETURNING`)
        return;
    }
    if (!foundSlap.place) {
        console.log(`SLAP HAS NO PLACE ${foundSlap.place} returning`)
    }
    const isDraft = foundSlap.isDraft ? true : false
    const isSubmitted = foundSlap.isSubmitted ? true : false
    const currentSticker = getStickerDesign({boardRef, stickerId: foundSlap.sticker})
    if (!currentSticker) {
        console.log(`NO STICKER DESIGN FOR ${foundSlap.sticker} RETURNING`)
        return;
    }
    const currentStickerUrl = getStickerUrl(currentSticker)
    const currentStickerAspectRatio = currentSticker.aspectRatio ? currentSticker.aspectRatio : 1

    const size = foundSlap.size ? foundSlap.size : 1
    const sizedBounds = BlockPlaces.sizedLngLatBoundsFromPlaceId(
        foundSlap.place,
        size
    )
    if (!sizedBounds) {
        console.log(`NO BOUNDS FOUND FOR PLACE ${foundSlap.place}`)
        return
    }
    const slapCoordinates = getPlaceFrameCoordinates({
        corner: sizedBounds.getSouthWest(),
        size: size,
        aspectRatio: currentStickerAspectRatio
    })
    const slapBounds = new LngLatBounds(
        [slapCoordinates[3].lng, slapCoordinates[3].lat], // new southwestern point
        [slapCoordinates[1].lng, slapCoordinates[1].lat]  // new northeastern point
    )

    // render source for slap
    const slapSourceIdString = sourceIdForSlap(foundSlap.id)
    const src = map.getSource(slapSourceIdString) as ImageSource
    if (src == undefined) {
        map.addSource(slapSourceIdString, {
            'type': 'image',
            'url': currentStickerUrl,
            'coordinates': [
                [slapCoordinates[0].lng, slapCoordinates[0].lat],
                [slapCoordinates[1].lng, slapCoordinates[1].lat],
                [slapCoordinates[2].lng, slapCoordinates[2].lat],
                [slapCoordinates[3].lng, slapCoordinates[3].lat],
            ]
        })
    }else{
        if (src.url != currentStickerUrl) {
            src.updateImage({
                'url': currentStickerUrl,
                'coordinates': [
                    [slapCoordinates[0].lng, slapCoordinates[0].lat],
                    [slapCoordinates[1].lng, slapCoordinates[1].lat],
                    [slapCoordinates[2].lng, slapCoordinates[2].lat],
                    [slapCoordinates[3].lng, slapCoordinates[3].lat],
                ]
            })
        }

    }
    // render layer for slap
    const slapLayerId = layerIdForSlap(foundSlap.id)
    const layer = map.getLayer(slapLayerId)
    if (layer == undefined) {
        console.log(`Adding slap layer for ${foundSlap.id}`)
        map.addLayer({
            'id': slapLayerId,
            'source': slapSourceIdString,
            'type': 'raster',
        })
    } else {
        map.setLayoutProperty(slapLayerId, 'visibility', 'visible')
    }

    // Display "PENDING" overlay on all draft slaps
    if (isDraft) {
        const pendingOverlaySourceId = pendingOverlaySourceIdForSlap(foundSlap.id)
        const pendingStampImageUrl = '/img/ui/pending-stamp.png'

        // Calculate overlay coordinates - position in upper-left of place region with fixed aspect ratio
        // Get the place bounds (not slap bounds) to position relative to the full place
        const placeOriginBounds = BlockPlaces.lngLatBoundsFromPlaceId(foundSlap.place)
        if (!placeOriginBounds) {
            console.log(`Cannot get place bounds for overlay positioning on place ${foundSlap.place}`)
            return
        }

        // Calculate overlay size - 80% of slap size (doubled from previous 40%)
        const slapWidth = Math.abs(slapCoordinates[1].lng - slapCoordinates[0].lng)
        const slapHeight = Math.abs(slapCoordinates[0].lat - slapCoordinates[3].lat)
        const overlayScale = 0.8 // 80% of slap size - twice as big as previous

        // Calculate overlay dimensions with same latitude compensation as main slap
        const overlaySize = slapWidth * overlayScale
        const overlayWidth = overlaySize

        // Apply Web Mercator latitude adjustment (same as getPlaceFrameCoordinates)
        const placeCorner = placeOriginBounds.getSouthWest()
        const latitudeAdjustment = Math.cos(placeCorner.lat * Math.PI / 180)
        const overlayHeight = overlaySize * latitudeAdjustment

        // Position in upper-left: 1/4 down from top, small fixed offset from left
        const placeNW = placeOriginBounds.getNorthWest()
        const placeSW = placeOriginBounds.getSouthWest()
        const placeHeight = Math.abs(placeNW.lat - placeSW.lat)

        const offsetFromLeft = overlayWidth * 0.15 // Small fixed offset from left edge (15% of overlay width)
        const offsetFromTop = overlayHeight * 0.15 // Small fixed offset from top edge (15% of overlay height)

        const overlayLeft = placeNW.lng - offsetFromLeft
        const overlayTop = placeNW.lat + offsetFromTop
        const overlayRight = overlayLeft + overlayWidth
        const overlayBottom = overlayTop - overlayHeight

        const roundNum = (n: number) => Number(n.toFixed(6))
        const overlayCoordinates = [
            new LngLat(roundNum(overlayLeft), roundNum(overlayTop)),
            new LngLat(roundNum(overlayRight), roundNum(overlayTop)),
            new LngLat(roundNum(overlayRight), roundNum(overlayBottom)),
            new LngLat(roundNum(overlayLeft), roundNum(overlayBottom)),
        ]

        const pendingOverlayImageSource = map.getSource(pendingOverlaySourceId) as ImageSource | undefined
        if (pendingOverlayImageSource == undefined) {
            map.addSource(pendingOverlaySourceId, {
                'type': 'image',
                'url': pendingStampImageUrl,
                'coordinates': [
                    [overlayCoordinates[0].lng, overlayCoordinates[0].lat],
                    [overlayCoordinates[1].lng, overlayCoordinates[1].lat],
                    [overlayCoordinates[2].lng, overlayCoordinates[2].lat],
                    [overlayCoordinates[3].lng, overlayCoordinates[3].lat],
                ]
            })
        } else {
            // Only update if the URL has changed (coordinates are stable)
            if (pendingOverlayImageSource.url !== pendingStampImageUrl) {
                pendingOverlayImageSource.updateImage({
                    'url': pendingStampImageUrl,
                    'coordinates': [
                        [overlayCoordinates[0].lng, overlayCoordinates[0].lat],
                        [overlayCoordinates[1].lng, overlayCoordinates[1].lat],
                        [overlayCoordinates[2].lng, overlayCoordinates[2].lat],
                        [overlayCoordinates[3].lng, overlayCoordinates[3].lat],
                    ]
                })
            }
        }

        const pendingOverlayLayerId = pendingOverlayLayerIdForSlap(foundSlap.id)
        const pendingOverlayLayer = map.getLayer(pendingOverlayLayerId)
        if (pendingOverlayLayer == undefined) {
            map.addLayer({
                id: pendingOverlayLayerId,
                type: 'raster',
                source: pendingOverlaySourceId,
                paint: {
                    'raster-fade-duration': 0,
                },
            })
        } else {
            // Ensure overlay is visible for draft slaps
            map.setLayoutProperty(pendingOverlayLayerId, 'visibility', 'visible')
        }
    }

    // Display commit transaction button on all pending slaps
    /* Temporarily commented out - evaluating UI without map commit button
    if (isDraft) {
        const placeExecuteButtonSourceId = execButtonSourceIdForSlap(foundSlap.id)
        const executeNowButtonImageUrl = '/img/ui/commit-slap-button-p1.png'
        const actionButtonCoordinates = coordinatesForPendingButton(slapBounds)
        const executeNowButtonImageSource = map.getSource(placeExecuteButtonSourceId) as ImageSource | undefined
        if (executeNowButtonImageSource == undefined) {
            map.addSource(placeExecuteButtonSourceId, {
                'type': 'image',
                'url': executeNowButtonImageUrl,
                'coordinates':  [
                    [actionButtonCoordinates[0].lng, actionButtonCoordinates[0].lat],
                    [actionButtonCoordinates[1].lng, actionButtonCoordinates[1].lat],
                    [actionButtonCoordinates[2].lng, actionButtonCoordinates[2].lat],
                    [actionButtonCoordinates[3].lng, actionButtonCoordinates[3].lat],
                ]
            })
        }

        const placeExecuteButtonLayerId = execButtonLayerIdForSlap(foundSlap.id)
        const actionButtonLayer = map.getLayer(placeExecuteButtonLayerId)
        if (actionButtonLayer == undefined) {
            map.addLayer({
                id: placeExecuteButtonLayerId,
                type: 'raster',
                source: placeExecuteButtonSourceId
            })
        }
        map.moveLayer(placeExecuteButtonLayerId)
    */
    if (isDraft) {
        if (isSubmitted) {
            // Hide pending overlay when slap is submitted
            const pendingOverlayLayerId = pendingOverlayLayerIdForSlap(foundSlap.id)
            if (map.getLayer(pendingOverlayLayerId)) {
                map.setLayoutProperty(pendingOverlayLayerId, 'visibility', 'none')
            }

            // Temporarily commented out since commit button is disabled
            // map.setLayoutProperty(placeExecuteButtonLayerId, 'visibility', 'none')
            // create an image with in progress ui image
            const inProgressLabelSourceId = inProgressLabelSourceIdForSlap(foundSlap.id)
            const inProgressLabelLayerId = inProgressLabelLayerIdForSlap(foundSlap.id)
            const inProgressLabelImageUrl = '/img/ui/hourglass-icon-p1.png'
            const inProgressLabelCoordinates = coordinatesForInProgressLabelInPlace(slapBounds)
            const inProgressLabelImageSource = map.getSource(inProgressLabelSourceId) as ImageSource | undefined
            if (inProgressLabelImageSource == undefined) {
                map.addSource(inProgressLabelSourceId, {
                    'type': 'image',
                    'url': inProgressLabelImageUrl,
                    'coordinates':  [
                        [inProgressLabelCoordinates[0].lng, inProgressLabelCoordinates[0].lat],
                        [inProgressLabelCoordinates[1].lng, inProgressLabelCoordinates[1].lat],
                        [inProgressLabelCoordinates[2].lng, inProgressLabelCoordinates[2].lat],
                        [inProgressLabelCoordinates[3].lng, inProgressLabelCoordinates[3].lat],
                    ]
                })
            }
            const inProgressLabelLayer = map.getLayer(inProgressLabelLayerId)
            if (inProgressLabelLayer == undefined) {
                map.addLayer({
                    id: inProgressLabelLayerId,
                    type: 'raster',
                    source: inProgressLabelSourceId
                })
            }
            map.moveLayer(inProgressLabelLayerId)
        }
    }
}

export const cleanupOrphanedSlapLayers = (props: {
    mapRef?: MutableRefObject<MapRef | null>
    boardRef: MutableRefObject<Board>
}) => {
    const { mapRef, boardRef } = props
    if (!mapRef || !mapRef.current) {
        return
    }
    const map = mapRef.current.getMap()
    if (!map) {
        return
    }
    const style = map.getStyle()
    if (!style || !style.layers) {
        return
    }

    const slapLayerIds = style.layers
        .map(layer => extractSlapIdFromLayerId(layer.id))
        .filter(id => id !== null) as number[]

    slapLayerIds.forEach((slapId) => {
        const slap = lookupSlap({ boardRef, slapId })
        if (slap) {
            return
        }
        const slapLayerId = layerIdForSlap(slapId)
        const slapSourceId = sourceIdForSlap(slapId)
        const execLayerId = execButtonLayerIdForSlap(slapId)
        const execSourceId = execButtonSourceIdForSlap(slapId)
        const inProgressLayerId = inProgressLabelLayerIdForSlap(slapId)
        const inProgressSourceId = inProgressLabelSourceIdForSlap(slapId)
        const pendingOverlayLayer = pendingOverlayLayerIdForSlap(slapId)
        const pendingOverlaySource = pendingOverlaySourceIdForSlap(slapId)

        if (map.getLayer(slapLayerId)) {
            map.removeLayer(slapLayerId)
        }
        if (map.getLayer(execLayerId)) {
            map.removeLayer(execLayerId)
        }
        if (map.getLayer(inProgressLayerId)) {
            map.removeLayer(inProgressLayerId)
        }
        if (map.getLayer(pendingOverlayLayer)) {
            map.removeLayer(pendingOverlayLayer)
        }

        if (map.getSource(slapSourceId)) {
            map.removeSource(slapSourceId)
        }
        if (map.getSource(execSourceId)) {
            map.removeSource(execSourceId)
        }
        if (map.getSource(inProgressSourceId)) {
            map.removeSource(inProgressSourceId)
        }
        if (map.getSource(pendingOverlaySource)) {
            map.removeSource(pendingOverlaySource)
        }
    })
}

export const setSlapLayerVisibility = (props: {
    mapRef?: MutableRefObject<MapRef | null>
    slapId: SlapId
    visibility: 'visible' | 'none'
}) => {
    const { mapRef, slapId, visibility } = props
    if (!mapRef || !mapRef.current) {
        return
    }
    const map = mapRef.current.getMap()
    if (!map) {
        return
    }
    const slapLayerId = layerIdForSlap(slapId)
    if (map.getLayer(slapLayerId)) {
        map.setLayoutProperty(slapLayerId, 'visibility', visibility)
    }
    const pendingOverlayLayerId = pendingOverlayLayerIdForSlap(slapId)
    if (map.getLayer(pendingOverlayLayerId)) {
        map.setLayoutProperty(pendingOverlayLayerId, 'visibility', visibility)
    }
    const inProgressLabelLayerId = inProgressLabelLayerIdForSlap(slapId)
    if (map.getLayer(inProgressLabelLayerId)) {
        map.setLayoutProperty(inProgressLabelLayerId, 'visibility', visibility)
    }
    const execButtonLayerId = execButtonLayerIdForSlap(slapId)
    if (map.getLayer(execButtonLayerId)) {
        map.setLayoutProperty(execButtonLayerId, 'visibility', visibility)
    }
}
